import { useCallback, useEffect, useRef, useState } from "react";
import { Bookmark, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, posterUrl } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { SwipeCard } from "@/components/SwipeCard";
import { SwipeActions } from "@/components/SwipeActions";
import { MovieDetails } from "@/components/MovieDetails";
import { WatchlistFilterBanner } from "@/components/WatchlistFilterBanner";
import { WatchOptions } from "@/components/WatchOptions";

const WL_DIRS = ["left", "down", "up"];
const WL_TOAST = { up: "Moved to Seen · Liked", down: "Moved to Seen · Didn't like", left: "Removed from watchlist" };

function PosterTile({ movie, onOpen, index }) {
  return (
    <button onClick={() => onOpen(movie)} data-testid={`watchlist-item-${movie.movie_id}`} style={{ animationDelay: `${index * 30}ms` }}
      className="fade-up group relative aspect-[2/3] rounded-2xl overflow-hidden border bg-card text-left">
      <img src={posterUrl(movie.poster_path, "w342")} alt={movie.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />
      <span className="absolute top-2 right-2 font-mono-sp text-[11px] px-2 py-1 rounded-full bg-black/60 backdrop-blur text-white" data-testid={`watchlist-score-${movie.movie_id}`}>
        {movie.score}% match
      </span>
      <div className="absolute bottom-0 p-3 text-white">
        <p className="font-display font-bold leading-tight line-clamp-2">{movie.title}</p>
        <p className="text-xs text-white/60">{movie.year} · {movie.genres[0] || "—"} · <span data-testid={`watchlist-cert-${movie.movie_id}`}>{movie.certification}</span></p>
      </div>
    </button>
  );
}

function WatchlistDialog({ movie, onClose, onDone, onDetails }) {
  const cardRef = useRef(null);
  return (
    <Dialog open={!!movie} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm max-h-[94vh] overflow-y-auto bg-transparent border-0 shadow-none p-0" data-testid="watchlist-dialog">
        <DialogTitle className="sr-only">{movie?.title}</DialogTitle>
        <DialogDescription className="sr-only">Swipe up if you liked it, down if not, left to remove.</DialogDescription>
        {movie && (
          <div className="flex flex-col gap-5">
            <div className="relative w-full h-[440px] max-h-[56vh] shrink-0">
              <SwipeCard ref={cardRef} movie={movie} allowed={WL_DIRS} onSwipe={(d) => onDone(movie, d)} onDetails={onDetails} />
            </div>
            <SwipeActions order={WL_DIRS} onAction={(d) => cardRef.current?.fly(d)} />
            <div className="rounded-3xl border bg-card p-4"><WatchOptions movieId={movie.movie_id} /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Watchlist() {
  const [sort, setSort] = useState("score");
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);
  const [details, setDetails] = useState(null);

  const [view, setView] = useState("active");
  const [meta, setMeta] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/watchlist", { params: { sort, view } });
    setItems(data.items);
    setMeta(data);
  }, [sort, view]);
  useEffect(() => { load(); }, [load]);

  const done = async (movie, direction) => {
    setActive(null);
    setItems((xs) => xs.filter((m) => m.movie_id !== movie.movie_id));
    try {
      await api.post("/swipe", { movie_id: movie.movie_id, direction, source: "watchlist" });
      toast(WL_TOAST[direction], {
        description: movie.title,
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/swipe/undo", { movie_id: movie.movie_id });
              toast("Back on your watchlist", { description: movie.title, duration: 1400 });
              load();
            } catch (err) {
              toast.error(formatApiError(err));
            }
          },
        },
      });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader eyebrow="Up next" title="Watchlist" sub="Tap a movie once you've watched it — swipe up if you liked it, down if not.">
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-[220px] rounded-full h-11" data-testid="watchlist-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="score" data-testid="watchlist-sort-score">Predicted enjoyment</SelectItem>
            <SelectItem value="date" data-testid="watchlist-sort-date">Date added</SelectItem>
            <SelectItem value="genre" data-testid="watchlist-sort-genre">Genre</SelectItem>
          </SelectContent>
        </Select>
      </PageHeader>
      {meta && meta.total > 0 && <WatchlistFilterBanner data={{ ...meta, items: items || [] }} onView={setView} />}
      {items === null ? (
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      ) : items.length === 0 && meta?.total > 0 ? (
        <div className="rounded-3xl border border-dashed p-12 text-center text-muted-foreground" data-testid="watchlist-filter-empty">
          No watchlist movies match this filter.{" "}
          <button onClick={() => setView("all")} className="text-primary font-semibold" data-testid="watchlist-filter-empty-show-all">Show all</button>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-dashed p-12 text-center text-muted-foreground" data-testid="watchlist-empty">
          <Bookmark className="h-8 w-8 mx-auto mb-3" />
          Nothing here yet. <Link to="/" className="text-primary font-semibold">Swipe right</Link> on movies you want to watch.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6" data-testid="watchlist-grid">
          {items.map((m, i) => <PosterTile key={m.movie_id} movie={m} index={i} onOpen={setActive} />)}
        </div>
      )}
      <WatchlistDialog movie={active} onClose={() => setActive(null)} onDone={done} onDetails={setDetails} />
      <MovieDetails movie={details} onClose={() => setDetails(null)} />
    </div>
  );
}
